/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Nullable, Path, RMADetailed, RMANote } from "../../utils/Interfaces";

import {
    Box,
    Typography,
    Stack,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    Button,
    Tooltip,
    IconButton,
    Grid,
    Divider,
} from "@mui/material";

import { NavigateFunction, useNavigate, useParams } from "react-router-dom";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../app/hooks";
import { addNoteToRMA, closeRMA, deleteRMA, editRMA, getRMADetails } from "./materialsRMAFunctions";
import { Cancel, Check, DateRange, Delete, Edit, Person } from "@mui/icons-material";
import RMANoteCard from "./RMANoteCard";
import ConfirmDeleteModal from "../../components/confirmation/ConfirmDeleteModal";
import { NumericFormat } from "react-number-format";
import AttachmentManager from "../../components/general/AttachmentManager";
import { ModelName } from "../../utils/Requests";

const MaterialsRMADetails = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const navigate: NavigateFunction = useNavigate();

    const [loading, setLoading] = useState<boolean>(false);
    const [rma, setRmaObject] = useState<Nullable<RMADetailed>>(null);
    const [newRMANote, setNewRMANote] = useState<string>("");

    const [editingRMA, setEditingRMA] = useState<boolean>(false);
    const [deleteRMAModalOpen, setDeleteRMAModalOpen] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submittingNote, setSubmittingNote] = useState<boolean>(false);

    const [updatedTitle, setUpdatedTitle] = useState<string>("");
    const [updatedDescription, setUpdatedDescription] = useState<string>("");
    const [updatedPartName, setUpdatedPartName] = useState<string>("");
    const [updatedManufacturer, setUpdatedManufacturer] = useState<string>("");
    const [updatedPartNumber, setUpdatedPartNumber] = useState<string>("");
    const [updatedSerialNumber, setUpdatedSerialNumber] = useState<string>("");
    const [updatedStoreNumber, setUpdatedStoreNumber] = useState<string>("");
    const [updatedPrice, setUpdatedPrice] = useState<number>(0.0);

    const { rmaId } = useParams();

    function setRma(rmaObj: RMADetailed) {
        setRmaObject(rmaObj);
        setSubmitting(false);
        setUpdatedTitle(rmaObj.title);
        setUpdatedDescription(rmaObj.description);
        setUpdatedPartName(rmaObj.name);
        setUpdatedManufacturer(rmaObj.manufacturer);
        setUpdatedPartNumber(rmaObj.part_number);
        setUpdatedSerialNumber(rmaObj.serial_number);
        setUpdatedStoreNumber(rmaObj.store_number);
        setUpdatedPrice(rmaObj.price);
    }

    useEffect(() => {
        if (rmaId === null || isNaN(parseInt(rmaId as string))) {
            return;
        }
        getRMADetails(dispatch, parseInt(rmaId as string), setRma, setLoading);
    }, [rmaId, dispatch]);

    let breadcrumbPaths: Nullable<Path>[] = [
        { text: "Techwise Portal", route: "/" },
        { text: "Parts/Materials", route: "/?section=materials" },
        rma === null
            ? null
            : {
                  text: rma?.date_closed === null ? "RMA - Open" : "RMA - Archive",
                  route: `/materials/rma?status=${rma?.date_closed === null ? "OP" : "AR"}`,
              },
        rma === null ? null : { text: `${rma.title}`, route: `/materials/rma/${rma.id}` },
    ];

    return (
        <Box>
            <StyledBreadcrumbs paths={breadcrumbPaths} />

            <ConfirmDeleteModal
                confirmText="delete RMA"
                description="Delete this RMA thread and all associated notes. This cannot be undone and will remove all data from the system."
                onDelete={() => {
                    deleteRMA(
                        dispatch,
                        rma?.id as number,
                        () => {
                            navigate(`/materials/rma?status=${rma?.date_closed === null ? "OP" : "AR"}`);
                        },
                        () => {}
                    );
                }}
                open={deleteRMAModalOpen}
                handleClose={() => setDeleteRMAModalOpen(false)}
            />

            {loading && rma === null && (
                <Stack direction="row" justifyContent="center" width="100%" marginTop={20}>
                    <Stack direction="column" justifyContent="center">
                        <CircularProgress size={100} />
                    </Stack>
                </Stack>
            )}

            {rma !== null && (
                <>
                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                {editingRMA && (
                                    <TextField
                                        disabled={submitting || rma.date_closed !== null}
                                        fullWidth
                                        size="small"
                                        sx={{ mr: 3 }}
                                        value={updatedTitle}
                                        onChange={(event) => setUpdatedTitle(event.target.value)}
                                        label="Edit RMA Title"
                                        name="edit_rma_title"
                                    />
                                )}
                                {!editingRMA && <Typography variant="h5">{rma.title}</Typography>}
                                {submitting && (
                                    <Box>
                                        <CircularProgress size={16} sx={{ ml: 6, mt: 1.5, mr: 2 }} />
                                    </Box>
                                )}
                                {!submitting && (
                                    <Stack direction="row" justifyContent="space-between">
                                        <Box>
                                            {!editingRMA && (
                                                <Tooltip title="Edit RMA.">
                                                    <IconButton
                                                        disabled={rma.date_closed !== null}
                                                        onClick={() => setEditingRMA(true)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {editingRMA && (
                                                <Tooltip title="Cancel Changes.">
                                                    <IconButton
                                                        onClick={() => {
                                                            setEditingRMA(false);
                                                            setUpdatedTitle(rma?.title);
                                                            setUpdatedDescription(rma?.description);
                                                            setUpdatedPartName(rma?.name);
                                                            setUpdatedManufacturer(rma?.manufacturer);
                                                            setUpdatedPartNumber(rma?.part_number);
                                                            setUpdatedSerialNumber(rma?.serial_number);
                                                            setUpdatedPrice(rma?.price);
                                                            setUpdatedStoreNumber(rma?.store_number);
                                                        }}
                                                    >
                                                        <Cancel />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Box>
                                            {!editingRMA && (
                                                <Tooltip title="Delete RMA.">
                                                    <IconButton color="error" onClick={() => setDeleteRMAModalOpen(true)}>
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {editingRMA && (
                                                <Tooltip title="Submit Changes.">
                                                    <IconButton
                                                        onClick={() => {
                                                            setSubmitting(true);
                                                            editRMA(
                                                                dispatch,
                                                                rma.id as number,

                                                                updatedTitle,
                                                                updatedDescription,
                                                                updatedPartNumber,
                                                                updatedManufacturer,
                                                                updatedPartNumber,
                                                                updatedSerialNumber,
                                                                updatedStoreNumber,
                                                                updatedPrice,

                                                                () =>
                                                                    getRMADetails(
                                                                        dispatch,
                                                                        parseInt(rmaId as string),
                                                                        (rmaObj: RMADetailed) => {
                                                                            setRma(rmaObj);
                                                                            setEditingRMA(false);
                                                                        },
                                                                        setLoading
                                                                    ),
                                                                setLoading
                                                            );

                                                            setUpdatedTitle(rma?.title);
                                                            setUpdatedDescription(rma?.description);
                                                        }}
                                                    >
                                                        <Check />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Stack>
                                )}
                            </Stack>

                            {editingRMA && (
                                <TextField
                                    disabled={submitting || rma.date_closed !== null}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    sx={{ mr: 3, mt: 3, mb: 2 }}
                                    value={updatedDescription}
                                    onChange={(event) => setUpdatedDescription(event.target.value)}
                                    label="Edit RMA Description"
                                    name="edit_rma_description"
                                />
                            )}
                            {!editingRMA && <Typography sx={{ mt: 2, mb: 2 }}>{rma.description}</Typography>}

                            <Grid container>
                                <Grid item xs={6} sx={{ pr: 1.5 }}>
                                    {editingRMA && (
                                        <TextField
                                            disabled={submitting || rma.date_closed !== null}
                                            fullWidth
                                            size="small"
                                            sx={{ mr: 3, mt: 3 }}
                                            value={updatedPartName}
                                            onChange={(event) => setUpdatedPartName(event.target.value)}
                                            label="Edit Part Name"
                                            name="edit_part_name"
                                        />
                                    )}
                                    {!editingRMA && (
                                        <Typography sx={{ mt: 2 }}>
                                            Part Name: <b>{rma.name === null || rma.name === "" ? "--" : rma.name}</b>
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6} sx={{ pl: 1.5 }}>
                                    {editingRMA && (
                                        <TextField
                                            disabled={submitting || rma.date_closed !== null}
                                            fullWidth
                                            size="small"
                                            sx={{ mr: 3, mt: 3 }}
                                            value={updatedManufacturer}
                                            onChange={(event) => setUpdatedManufacturer(event.target.value)}
                                            label="Edit Manufacturer"
                                            name="edit_manufacturer"
                                        />
                                    )}
                                    {!editingRMA && (
                                        <Typography sx={{ mt: 2 }}>
                                            Manufacturer:{" "}
                                            <b>
                                                {rma.manufacturer === null || rma.manufacturer === "" ? "--" : rma.manufacturer}
                                            </b>
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6} sx={{ pr: 1.5 }}>
                                    {editingRMA && (
                                        <TextField
                                            disabled={submitting || rma.date_closed !== null}
                                            fullWidth
                                            size="small"
                                            sx={{ mr: 3, mt: 3 }}
                                            value={updatedPartNumber}
                                            onChange={(event) => setUpdatedPartNumber(event.target.value)}
                                            label="Edit Part Number"
                                            name="edit_part_number"
                                        />
                                    )}
                                    {!editingRMA && (
                                        <Typography sx={{ mt: 2 }}>
                                            Part #:{" "}
                                            <b>{rma.part_number === null || rma.part_number === "" ? "--" : rma.part_number}</b>
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6} sx={{ pl: 1.5 }}>
                                    {editingRMA && (
                                        <TextField
                                            disabled={submitting || rma.date_closed !== null}
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 3 }}
                                            value={updatedSerialNumber}
                                            onChange={(event) => setUpdatedSerialNumber(event.target.value)}
                                            label="Edit Serial Number"
                                            name="edit_serial_number"
                                        />
                                    )}
                                    {!editingRMA && (
                                        <Typography sx={{ mt: 2 }}>
                                            Serial #:{" "}
                                            <b>
                                                {rma.serial_number === null || rma.serial_number === ""
                                                    ? "--"
                                                    : rma.serial_number}
                                            </b>
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6}>
                                    {editingRMA && (
                                        <Box sx={{ pt: 2, ml: -1, pr: 2.5 }}>
                                            <NumericFormat
                                                size="small"
                                                allowNegative={false}
                                                value={updatedPrice}
                                                customInput={TextField}
                                                style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                                label="Price"
                                                thousandSeparator=","
                                                decimalScale={2}
                                                prefix="$ "
                                                onValueChange={(values) => {
                                                    setUpdatedPrice(values.floatValue ?? 0.0);
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {!editingRMA && (
                                        <Typography sx={{ mt: 2 }}>
                                            Acquisition Price: <b>${rma.price === null ? "--" : rma.price}</b>
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6}>
                                    {editingRMA && (
                                        <TextField
                                            disabled={submitting || rma.date_closed !== null}
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 3, ml: 1.5, pr: 1.5 }}
                                            value={updatedStoreNumber}
                                            onChange={(event) => setUpdatedStoreNumber(event.target.value)}
                                            label="Edit Store #"
                                            name="edit_store_number"
                                        />
                                    )}
                                    {!editingRMA && (
                                        <Typography sx={{ mt: 2, ml: 1.5 }}>
                                            Store #:{" "}
                                            <b>
                                                {rma.store_number === null || rma.store_number === "" ? "--" : rma.store_number}
                                            </b>
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>

                            <Divider sx={{ mt: 3, mb: 2 }}>Attachments</Divider>
                            <AttachmentManager model={ModelName.RMA} idKey={rma?.id} editing={true} readOnly={false} />

                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                                <Stack direction="column" justifyContent="flex-start">
                                    <Stack direction="row" justifyContent="flex-start">
                                        <Person fontSize="small" />
                                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                                            {rma.reporter?.first_name ?? "--"}&nbsp;{rma.reporter?.last_name}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="flex-start">
                                        <DateRange fontSize="small" />
                                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                                            Opened&nbsp;•&nbsp;
                                            {new Date(rma.date_opened).toLocaleDateString()}&nbsp;
                                            {new Date(rma.date_opened).toLocaleTimeString()}
                                        </Typography>
                                    </Stack>
                                    {rma.date_closed !== null && (
                                        <Stack direction="row" justifyContent="flex-start" sx={{ mb: -1.5 }}>
                                            <DateRange fontSize="small" />
                                            <Typography variant="subtitle2" sx={{ ml: 1 }}>
                                                Closed&nbsp;•&nbsp;
                                                {new Date(rma.date_closed as string).toLocaleDateString()}&nbsp;
                                                {new Date(rma.date_closed as string).toLocaleTimeString()}
                                            </Typography>
                                        </Stack>
                                    )}

                                    {submitting && <CircularProgress size={16} sx={{ mt: 4.5, mb: -0.5, ml: 11 }} />}

                                    {!submitting && rma.date_closed === null && (
                                        <Button
                                            sx={{ mt: 3, mb: -1.5 }}
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                setSubmitting(true);
                                                closeRMA(
                                                    dispatch,
                                                    rma.id,
                                                    () => {
                                                        setSubmitting(false);
                                                        getRMADetails(dispatch, parseInt(rmaId as string), setRma, setLoading);
                                                    },
                                                    setLoading
                                                );
                                            }}
                                        >
                                            Close RMA Thread
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                    <Typography variant="h5" sx={{ mt: 3, ml: 1 }}>
                        Notes
                    </Typography>
                    {rma.notes.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            {rma.notes.map((rmaNote: RMANote, index: number) => {
                                return (
                                    <RMANoteCard
                                        rma={rma}
                                        rmaNote={rmaNote}
                                        index={index}
                                        onRMANoteUpdated={() =>
                                            getRMADetails(dispatch, parseInt(rmaId as string), setRma, setLoading)
                                        }
                                    />
                                );
                            })}
                        </Box>
                    )}

                    {rma.date_closed === null && (
                        <>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ mt: 3 }}
                                value={newRMANote}
                                onChange={(event) => setNewRMANote(event.target.value)}
                                label="Add RMA Notes"
                                name="rma_notes"
                            />

                            {submittingNote && <CircularProgress size={16} sx={{ mt: 3.5, ml: 4 }} />}

                            {!submittingNote && (
                                <Button
                                    sx={{ mt: 3 }}
                                    variant="contained"
                                    disabled={newRMANote === ""}
                                    onClick={() => {
                                        setSubmittingNote(true);
                                        addNoteToRMA(
                                            dispatch,
                                            rma?.id,
                                            newRMANote,
                                            () => {
                                                getRMADetails(dispatch, parseInt(rmaId as string), setRma, setLoading);
                                                setNewRMANote("");
                                                setSubmittingNote(false);
                                            },
                                            () => {}
                                        );
                                    }}
                                >
                                    Add Note
                                </Button>
                            )}
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default MaterialsRMADetails;
